import { createContext, ReactNode, useCallback, useContext } from 'react';

import {
  ProviderContext as NotistackProviderContext,
  SnackbarProvider as NotistackSnackbarProvider,
  useSnackbar as useNotistackSnackbar,
} from 'notistack';

// @ts-ignore - Ignore so we don't have to provide default values to the context
const SnackbarContext = createContext<NotistackProviderContext & {
  enqueueSnackbarError(err?: Error, msg?: string): void;
}>();

export const useSnackbar = () => useContext(SnackbarContext);

/**
 * SnackbarProvider provides both the notistack SnackbarProvider and an internal
 * provider to extend notistack
 */
const SnackbarProvider = (props: { children: ReactNode }) => {
  return (
    <NotistackSnackbarProvider
      maxSnack={3}
      autoHideDuration={5000}
      classes={{
        // We're preferring blue for our success-type messages (same as info), but we should keep these separate
        // in case we have second thoughts later
        variantSuccess: 'bg-blue-600 text-white',
        variantError: 'bg-error text-white',
        variantWarning: 'bg-yellow-500 text-black',
        variantInfo: 'bg-blue-600 text-white',
      }}
    >
      <SnackbarProviderInternal {...props} />
    </NotistackSnackbarProvider>
  );
};

/**
 * SnackbarProviderInternal is a global context provider that provides helper
 * functions to display notistack messages
 */
const SnackbarProviderInternal = ({ children }: { children: ReactNode }) => {
  const { closeSnackbar, enqueueSnackbar } = useNotistackSnackbar();

  const enqueueSnackbarError = useCallback((err?: Error, msg?: string) => {
    // TODO: Parse error and display user-friendly message accordingly
    // TODO: Send these to Bugsnag?
    const defaultErrorMessage = 'An unexpected error has occurred';
    enqueueSnackbar(msg || defaultErrorMessage, { variant: 'error' });
  }, [enqueueSnackbar]);

  return (
    <SnackbarContext.Provider value={{
      enqueueSnackbarError,
      enqueueSnackbar,
      closeSnackbar,
    }}>
      {children}
    </SnackbarContext.Provider>
  );
};

export default SnackbarProvider;
